import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-scroll";
import { HiMenuAlt3, HiX } from "react-icons/hi";

const NAV_LINKS = [
  { to: "hero", label: "Home" },
  { to: "about", label: "About" },
  { to: "menu", label: "Menu" },
  { to: "order", label: "Order Online" },
  { to: "gallery", label: "Gallery" },
  { to: "contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  return (
    <>
      {/* Nav Bar */}
      <nav
        role="navigation"
        aria-label="Main navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled || isOpen
            ? "bg-charcoal/95 backdrop-blur-md shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="hero" smooth duration={600} className="cursor-pointer flex items-center gap-2.5" aria-label="Go to homepage">
            <img
              src="/assets/PHBLogo.png"
              alt="PHB Logo"
              className="h-9 w-9 object-contain"
            />
            <h1
              className={`font-display text-2xl font-bold tracking-wide transition-colors duration-300 ${
                scrolled || isOpen ? "text-cream" : "text-white"
              }`}
            >
              The Pahadi Bistro
            </h1>
          </Link>

          {/* Desktop Links */}
          <ul className="hidden md:flex items-center space-x-8">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  smooth
                  duration={600}
                  offset={-70}
                  spy
                  activeClass="!text-gold"
                  className={`text-sm font-semibold tracking-wide cursor-pointer transition-colors duration-200 hover:text-gold ${
                    scrolled ? "text-cream/80" : "text-white/80"
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <a
                href="tel:+919373309484"
                className="inline-flex items-center gap-2 px-5 py-2 bg-redAccent text-white text-sm font-semibold rounded-full hover:bg-redDark transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Order Now
              </a>
            </li>
          </ul>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden relative z-[60] p-2"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <HiX className="w-7 h-7 text-cream" />
            ) : (
              <HiMenuAlt3
                className={`w-7 h-7 transition-colors ${
                  scrolled ? "text-cream" : "text-white"
                }`}
              />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Drawer — rendered outside nav, above everything */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-[55]">
          {/* Opaque overlay — fully blocks content behind */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={closeMenu}
          />

          {/* Drawer Panel */}
          <div className="absolute right-0 top-0 h-full w-72 bg-charcoal shadow-2xl">
            <div className="flex flex-col pt-24 px-8 space-y-2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  smooth
                  duration={600}
                  offset={-70}
                  className="text-cream/80 text-lg font-semibold py-3 px-4 rounded-lg hover:text-gold hover:bg-white/5 transition-all cursor-pointer"
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4">
                <a
                  href="tel:+919373309484"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-redAccent text-white font-semibold rounded-full hover:bg-redDark transition-colors"
                  onClick={closeMenu}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Order Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
